import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Plus, Filter, Download, Search, Edit, Trash2, FileSpreadsheet, FileText, UserCheck, Trash, X, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ExternalLink, Code, Palette, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TaskDetailModal from '../components/TaskDetailModal';
import MultiLinkInput from '../components/MultiLinkInput';
import UserAvatar from '../components/UserAvatar';
import { TaskIdBadge } from '../components/TaskIdBadge';
import { DatePicker } from '../components/DatePicker';
import { getBrandBadgeClasses } from '../utils/brandColors';
import { getContentTypeBadgeClasses } from '../utils/contentTypes';
import { useTaskOptions, canonicalKeyForName, nameForCanonicalKey } from '../utils/taskOptions';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { consumeTaskEditHandoff } from '../utils/taskEditHandoff';
import { useBrands } from '../utils/brands';
import { csvSafe } from '../utils/csvSafe';
import { matchesTaskSearch } from '../utils/taskSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sort indicator component
const SortIndicator = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return <ChevronsUpDown size={14} className="text-slate-400" />;
  }
  return sortDirection === 'asc' 
    ? <ChevronUp size={14} className="text-cyan-600" />
    : <ChevronDown size={14} className="text-cyan-600" />;
};

const TaskTable = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const BRANDS = useBrands();
  const { CATEGORIES: TASK_CATEGORIES, CONTENT_TYPES, TASK_STATUSES, APPROVAL_STATUSES, DEPENDENCY_TYPES, RAW: TASK_OPTIONS_RAW } = useTaskOptions();
  // Canonical defaults — resolved by canonical_key (not hardcoded name) so an
  // admin rename of "Yet to start"/"Pending" is reflected immediately.
  const DEFAULT_STATUS = nameForCanonicalKey(TASK_OPTIONS_RAW.task_statuses, 'yet_to_start', TASK_STATUSES[0] || 'Yet to start');
  const DEFAULT_APPROVAL_STATUS = nameForCanonicalKey(TASK_OPTIONS_RAW.manager_approval_statuses, 'pending', APPROVAL_STATUSES[0] || 'Pending');
  const DEFAULT_CATEGORY = TASK_CATEGORIES[0] || 'Technical SEO';
  const depTypeLabel = (key, fallback) => nameForCanonicalKey(TASK_OPTIONS_RAW.dependency_types, key, fallback);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Multi-select filters — each holds an array of selected values; an empty
  // array means "All" (no filtering on that field). OR within a filter,
  // AND across different filters (see filteredTasks below).
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterContentType, setFilterContentType] = useState([]);
  const [filterBrand, setFilterBrand] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterUser, setFilterUser] = useState([]);
  const [filterManagerApproval, setFilterManagerApproval] = useState([]);
  const [filterDependency, setFilterDependency] = useState([]);
  const [filterDateField, setFilterDateField] = useState('start_date');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
  const [reassignUserId, setReassignUserId] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  // Manual refresh state — mirrors the Manager Dashboard / Manager Approval
  // pattern so users can re-sync the table without losing filters, sorting,
  // search, pagination, or date ranges (all of those live in local state and
  // are derived from the freshly fetched `tasks` array).
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  
  // Bulk Update state
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false);
  const [isBulkConfirmDialogOpen, setIsBulkConfirmDialogOpen] = useState(false);
  const [bulkUpdateFields, setBulkUpdateFields] = useState({
    your_status: { enabled: false, value: '' },
    task_done_by: { enabled: false, value: '' },
    category: { enabled: false, value: '' },
    content_type: { enabled: false, value: '' },
    brand: { enabled: false, value: '' },
    manager_approval_status: { enabled: false, value: '' },
    manager_approval_required: { enabled: false, value: false },
    start_date: { enabled: false, value: '' },
    developer_dependency: { enabled: false, value: false },
    uiux_dependency: { enabled: false, value: false },
    notes: { enabled: false, value: '', mode: 'append' }
  });
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  
  // Sorting state - default to most recently created first
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Column resize state
  const [taskColumnWidth, setTaskColumnWidth] = useState(350);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = React.useRef(0);
  const resizeStartWidth = React.useRef(0);
  const isResizingRef = React.useRef(false);
  
  // Column resize handlers
  const handleResizeMove = React.useCallback((e) => {
    if (!isResizingRef.current) return;
    const diff = e.clientX - resizeStartX.current;
    const newWidth = Math.max(150, Math.min(800, resizeStartWidth.current + diff));
    setTaskColumnWidth(newWidth);
  }, []);
  
  const handleResizeEnd = React.useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);
  
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = taskColumnWidth;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  // Cleanup resize listeners on unmount
  React.useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);
  
  // Use centralized permissions from PermissionsContext (RBAC-backed).
  // `isReadOnly` stays on AuthContext because it is a user state, not a permission.
  const { isReadOnly } = useAuth();
  const { isManager, canDeleteTasks, canModifyTasks, can } = usePermissions();

  // Export-scope flag — mirrors the backend `EXPORT_ALL_ROLES` whitelist so the
  // UI shows "Export All Tasks" vs "Export My Tasks" correctly. Backend still
  // enforces this on the export routes regardless of what the UI shows.
  const canExportAll = ['Manager', 'Admin'].includes(user?.role_level);
  
  const [formData, setFormData] = useState({
    task: '',
    output: '',
    category: DEFAULT_CATEGORY,
    content_type: '',
    brand: '',
    your_status: DEFAULT_STATUS,
    task_done_by: '',
    start_date: '',
    developer_dependency: false,
    uiux_dependency: false,
    manager_approval_required: false,
    manager_approval_status: DEFAULT_APPROVAL_STATUS,
    manager_notes: '',
    links: [],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // If another module routed here via the "Edit Task" button in the shared
  // TaskDetailModal (Manager Approval / Developer Queue / UI/UX Queue / etc.),
  // a sessionStorage handoff token is waiting. Pop it and open the matching
  // task in edit mode so the user lands directly in the same edit dialog.
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;
    const taskId = consumeTaskEditHandoff();
    if (!taskId) return;
    const target = tasks.find(t => t.id === taskId);
    if (target) openEditDialog(target);
  }, [tasks]);

  // Deep link from a notification (?highlight=<task_id>) — open the exact
  // Task Details modal for that task. Fast path: reuse the already-loaded
  // `tasks` list (no extra call). Fallback: fetch the single task directly
  // — this re-enforces the same `_user_can_access_task` RBAC rule as every
  // other task read, so a stale/forged link can never show more than the
  // user is allowed to see from the Task Table itself. Keyed off
  // `location.search` (not a one-shot ref) so a second notification click
  // while already on /tasks — same route, same mounted component — still
  // re-triggers this effect. `handledHighlightIdRef` just prevents
  // re-opening the SAME id twice (e.g. once `tasks` finishes loading and
  // again after the fallback fetch resolves).
  const handledHighlightIdRef = React.useRef(null);
  useEffect(() => {
    if (loading) return;
    const highlightId = new URLSearchParams(location.search).get('highlight');
    if (!highlightId || handledHighlightIdRef.current === highlightId) return;
    handledHighlightIdRef.current = highlightId;

    (async () => {
      const existing = tasks.find(t => t.id === highlightId);
      if (existing) {
        setSelectedTask(existing);
        setIsDetailOpen(true);
      } else {
        try {
          const res = await axios.get(`${API}/tasks/${highlightId}`, { headers: { Authorization: `Bearer ${token}` } });
          setSelectedTask(res.data);
          setIsDetailOpen(true);
        } catch (error) {
          toast.error(
            error.response?.status === 404
              ? "This task no longer exists or you don't have access to it."
              : 'Failed to open the task from this notification.'
          );
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('highlight');
      navigate(url.pathname + url.search, { replace: true });
      // Reset so clicking the SAME notification again later (after the
      // param round-trips back in) still re-opens the modal instead of
      // being silently ignored because the ref still points at this id.
      handledHighlightIdRef.current = null;
    })();
  }, [tasks, loading, location.search]);

  const loadData = async () => {
    try {
      const tasksRes = await axios.get(`${API}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(tasksRes.data);
      setLastRefreshed(new Date());
    } catch (error) {
      toast.error('Failed to load tasks');
    }
    try {
      const usersRes = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(usersRes.data);
    } catch (error) {
      // Non-fatal — Task Table still renders with tasks even if the user
      // directory fails to load (assignee names just won't resolve).
      console.error('Failed to load users', error);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh — re-fetches tasks + users in parallel with cache-busting
  // headers / query param so the proxy and browser can't serve a stale 304.
  // Filters, search, sort, pagination, and date ranges are all kept in local
  // state above and naturally survive across the re-render. Toasts + a
  // disabled state prevent duplicate calls when users mash the button.
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const noCache = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        params: { _ts: Date.now() },
      };
      const [tasksRes, usersRes] = await Promise.all([
        axios.get(`${API}/tasks`, noCache),
        axios.get(`${API}/users`, noCache),
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setLastRefreshed(new Date());
      toast.success('Task Table refreshed successfully');
    } catch (error) {
      toast.error('Unable to refresh Task Table. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Required-field validation for Create / Edit Task modal.
  // Returns a { fieldName: message } object — empty object means valid.
  const validateTaskForm = (data) => {
    const errors = {};
    if (!data.task || !data.task.trim()) {
      errors.task = 'Task description is required';
    }
    if (!data.category) {
      errors.category = 'Category is required';
    }
    if (!data.brand) {
      errors.brand = 'Brand is required';
    }
    if (!data.your_status) {
      errors.your_status = 'Status is required';
    }
    if (!data.start_date) {
      errors.start_date = 'Start date is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run required-field validation. Block submit and surface red error states
    // (matches the rest of the application's inline error pattern).
    const errors = validateTaskForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }
    setFormErrors({});

    try {
      // Prepare data with properly formatted start_date
      const submitData = { ...formData };
      if (submitData.start_date) {
        submitData.start_date = new Date(submitData.start_date).toISOString();
      } else {
        submitData.start_date = null;
      }
      
      if (editingTask) {
        await axios.put(
          `${API}/tasks/${editingTask.id}`,
          { ...submitData, note_source: 'task_note' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task updated!');
      } else {
        await axios.post(
          `${API}/tasks`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task created!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      // FastAPI validation errors return `detail` as an array of objects —
      // rendering that directly as a toast child crashes React. Fall back
      // to a readable string in that case.
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? (detail.map(d => d.msg).filter(Boolean).join(', ') || 'Failed to save task')
        : 'Failed to save task';
      toast.error(message);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, mode: null, taskId: null });

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted!');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const requestDelete = (taskId) => {
    setDeleteConfirm({ open: true, mode: 'single', taskId });
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTasks.length === paginatedTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(paginatedTasks.map(t => t.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedTasks.map(taskId =>
          axios.delete(`${API}/tasks/${taskId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedTasks.length} task(s) deleted successfully!`);
      setSelectedTasks([]);
      loadData();
    } catch (error) {
      toast.error('Failed to delete tasks');
    }
  };

  const requestBulkDelete = () => {
    setDeleteConfirm({ open: true, mode: 'bulk', taskId: null });
  };

  const confirmDeleteDialog = () => {
    if (deleteConfirm.mode === 'bulk') {
      handleBulkDelete();
    } else if (deleteConfirm.taskId) {
      handleDelete(deleteConfirm.taskId);
    }
    setDeleteConfirm({ open: false, mode: null, taskId: null });
  };

  const handleBulkReassign = async () => {
    if (!reassignUserId) {
      toast.error('Please select a user to reassign tasks');
      return;
    }

    try {
      await Promise.all(
        selectedTasks.map(taskId =>
          axios.put(
            `${API}/tasks/${taskId}`,
            { task_done_by: reassignUserId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success(`${selectedTasks.length} task(s) reassigned successfully!`);
      setSelectedTasks([]);
      setIsReassignDialogOpen(false);
      setReassignUserId('');
      loadData();
    } catch (error) {
      toast.error('Failed to reassign tasks');
    }
  };

  const handleSingleReassign = async (taskId, newUserId) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { task_done_by: newUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task reassigned successfully!');
      loadData();
    } catch (error) {
      toast.error('Failed to reassign task');
    }
  };

  // Bulk Update Functions
  const resetBulkUpdateFields = () => {
    setBulkUpdateFields({
      your_status: { enabled: false, value: '' },
      task_done_by: { enabled: false, value: '' },
      category: { enabled: false, value: '' },
      content_type: { enabled: false, value: '' },
      brand: { enabled: false, value: '' },
      manager_approval_status: { enabled: false, value: '' },
      manager_approval_required: { enabled: false, value: false },
      start_date: { enabled: false, value: '' },
      developer_dependency: { enabled: false, value: false },
      uiux_dependency: { enabled: false, value: false },
      notes: { enabled: false, value: '', mode: 'append' }
    });
  };

  const openBulkUpdateDialog = () => {
    resetBulkUpdateFields();
    setIsBulkUpdateDialogOpen(true);
  };

  const closeBulkUpdateDialog = () => {
    setIsBulkUpdateDialogOpen(false);
    resetBulkUpdateFields();
  };

  const getEnabledBulkFields = () => {
    const enabledFields = {};
    Object.entries(bulkUpdateFields).forEach(([field, config]) => {
      if (config.enabled && (config.value !== '' || typeof config.value === 'boolean')) {
        enabledFields[field] = config.value;
      }
    });
    return enabledFields;
  };

  const hasEnabledBulkFields = () => {
    return Object.values(bulkUpdateFields).some(config => config.enabled);
  };

  const handleBulkUpdateConfirm = () => {
    if (!hasEnabledBulkFields()) {
      toast.error('Please select at least one field to update');
      return;
    }
    setIsBulkConfirmDialogOpen(true);
  };

  const handleBulkUpdateSubmit = async () => {
    setBulkUpdateLoading(true);
    
    try {
      const updates = getEnabledBulkFields();
      const notesMode = bulkUpdateFields.notes.mode || 'append';
      
      const response = await axios.post(
        `${API}/tasks/bulk-update`,
        {
          task_ids: selectedTasks,
          updates: updates,
          notes_mode: notesMode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      if (data.success) {
        if (data.failed_count > 0) {
          toast.warning(`Updated ${data.success_count} tasks. ${data.failed_count} failed.`);
        } else {
          toast.success(`Successfully updated ${data.success_count} task(s)!`);
        }
        setSelectedTasks([]);
        setIsBulkConfirmDialogOpen(false);
        setIsBulkUpdateDialogOpen(false);
        resetBulkUpdateFields();
        loadData();
      } else {
        toast.error(data.message || 'Bulk update failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to perform bulk update';
      toast.error(errorMessage);
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  const getBulkFieldLabel = (field) => {
    const labels = {
      your_status: 'Status',
      task_done_by: 'Assigned User',
      category: 'Category',
      content_type: 'Content Type',
      brand: 'Brand',
      manager_approval_status: 'Manager Approval Status',
      manager_approval_required: 'Require Manager Approval',
      start_date: 'Start Date',
      developer_dependency: 'Developer Dependency',
      uiux_dependency: 'UI/UX Dependency',
      notes: 'Notes'
    };
    return labels[field] || field;
  };

  const getBulkFieldValue = (field, value) => {
    if (field === 'task_done_by') {
      const user = users.find(u => u.id === value);
      return user?.full_name || value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return value;
  };

  const resetForm = () => {
    setFormData({
      task: '',
      output: '',
      category: DEFAULT_CATEGORY,
      content_type: '',
      brand: '',
      your_status: DEFAULT_STATUS,
      task_done_by: user?.id || '', // Auto-select current user
      start_date: '',
      developer_dependency: false,
      uiux_dependency: false,
      manager_approval_required: false,
      manager_approval_status: DEFAULT_APPROVAL_STATUS,
      manager_notes: '',
      links: [],
      notes: ''
    });
    setEditingTask(null);
    setFormErrors({});
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    // Migrate legacy link to links array if needed
    let taskLinks = task.links || [];
    if (!taskLinks.length && task.link) {
      taskLinks = [{ url: task.link, label: 'Link' }];
    }
    setFormData({
      task: task.task,
      output: task.output || '',
      category: task.category,
      content_type: task.content_type || '',
      brand: task.brand || '',
      your_status: task.your_status,
      task_done_by: task.task_done_by || '',
      start_date: task.start_date ? format(new Date(task.start_date), 'yyyy-MM-dd') : '',
      developer_dependency: task.developer_dependency || false,
      uiux_dependency: task.uiux_dependency || false,
      links: taskLinks,
      notes: task.notes || ''
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchesSearch = matchesTaskSearch(task, searchTerm);
      const matchesCategory = filterCategory.length === 0 || filterCategory.includes(task.category);
      const matchesContentType = filterContentType.length === 0 || filterContentType.includes(task.content_type);
      const matchesBrand = filterBrand.length === 0 || filterBrand.includes(task.brand);
      const matchesStatus = filterStatus.length === 0 || filterStatus.includes(task.your_status);
      
      // User filter with "unassigned" option — OR across selected users.
      const matchesUser = filterUser.length === 0 ||
        filterUser.some((v) => (v === 'unassigned' && !task.task_done_by) || task.task_done_by === v);
      
      // Manager approval filter — canonical_key based so an admin rename of
      // Approved/Standby/Rejected/Pending/Not Required doesn't silently
      // break this filter. OR across selected statuses.
      const matchesManagerApproval = filterManagerApproval.length === 0 || filterManagerApproval.some((v) => {
        const key = canonicalKeyForName(TASK_OPTIONS_RAW.manager_approval_statuses, v);
        if (key === 'not_required') return task.manager_approval_required !== true;
        return task.manager_approval_required === true && canonicalKeyForName(TASK_OPTIONS_RAW.manager_approval_statuses, task.manager_approval_status) === key;
      });
      
      // Unified dependency filter — EXCLUSIVE matching per value, OR'd across selections.
      // Developer: dev=true AND uiux=false; UI/UX: uiux=true AND dev=false; Both: dev=true AND uiux=true; None: neither
      const hasDev = task.developer_dependency === true;
      const hasUIUX = task.uiux_dependency === true;
      const matchesDependency = filterDependency.length === 0 || filterDependency.some((v) => {
        if (v === 'developer') return hasDev && !hasUIUX;
        if (v === 'uiux') return hasUIUX && !hasDev;
        if (v === 'both') return hasDev && hasUIUX;
        if (v === 'none') return !hasDev && !hasUIUX;
        return false;
      });
      
      // Date range filter based on selected date field
      let matchesDateRange = true;
      if (filterFromDate || filterToDate) {
        let taskDate = null;
        if (filterDateField === 'start_date') {
          taskDate = task.start_date ? new Date(task.start_date) : null;
        } else if (filterDateField === 'created_at') {
          taskDate = task.created_at ? new Date(task.created_at) : null;
        } else if (filterDateField === 'updated_at') {
          taskDate = task.updated_at ? new Date(task.updated_at) : null;
        }
        
        if (!taskDate) {
          matchesDateRange = false;
        } else {
          const fromDate = filterFromDate ? new Date(filterFromDate) : null;
          const toDate = filterToDate ? new Date(filterToDate) : null;
          
          // Set to end of day for 'to' date comparison
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
          }
          
          if (fromDate && toDate) {
            matchesDateRange = taskDate >= fromDate && taskDate <= toDate;
          } else if (fromDate) {
            matchesDateRange = taskDate >= fromDate;
          } else if (toDate) {
            matchesDateRange = taskDate <= toDate;
          }
        }
      }
      
      return matchesSearch && matchesCategory && matchesContentType && matchesBrand && matchesStatus && matchesUser && 
             matchesManagerApproval && matchesDependency && matchesDateRange;
    });
  }, [tasks, searchTerm, filterCategory, filterContentType, filterBrand, filterStatus, filterUser, filterManagerApproval, 
      filterDependency, filterDateField, filterFromDate, filterToDate]);

  // Sorted tasks
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks].sort((a, b) => {
      // Unified dependency sort — order by count of dependencies (0 -> 2)
      if (sortField === 'dependency') {
        const score = (t) => (t.developer_dependency ? 1 : 0) + (t.uiux_dependency ? 1 : 0);
        const aScore = score(a);
        const bScore = score(b);
        if (aScore === bScore) return 0;
        return sortDirection === 'asc' ? aScore - bScore : bScore - aScore;
      }

      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
      if (bVal == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle date fields
      if (['created_at', 'updated_at', 'start_date', 'your_end_date', 'developer_end_date'].includes(sortField)) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle boolean fields
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      }
      
      // Handle string comparisons
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTasks, sortField, sortDirection]);

  // Paginated tasks
  const totalPages = Math.ceil(sortedTasks.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedTasks = sortedTasks.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterContentType, filterStatus, filterUser, filterManagerApproval, 
      filterDependency, filterDateField, filterFromDate, filterToDate, sortField, sortDirection, rowsPerPage]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || filterCategory.length > 0 || filterContentType.length > 0 || filterBrand.length > 0 || filterStatus.length > 0 || 
    filterUser.length > 0 || filterManagerApproval.length > 0 || filterDependency.length > 0 ||
    filterFromDate || filterToDate;

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterCategory([]);
    setFilterContentType([]);
    setFilterBrand([]);
    setFilterStatus([]);
    setFilterUser([]);
    setFilterManagerApproval([]);
    setFilterDependency([]);
    setFilterDateField('start_date');
    setFilterFromDate('');
    setFilterToDate('');
    toast.success('All filters cleared');
  };

  const clearDateFilters = () => {
    setFilterFromDate('');
    setFilterToDate('');
    toast.success('Date filters cleared');
  };

  const handleServerExport = async (format) => {
    try {
      // UX guard — if a regular user has set the "Assigned User" filter to
      // someone other than themselves, the backend will still strip it down
      // to their own rows. Surface that before the download so the file isn't
      // mysteriously empty / unexpectedly small.
      if (!canExportAll && filterUser.length > 0 && filterUser.some((v) => v !== user?.id)) {
        toast.warning(
          'Your role can only export your own tasks. The selected user filter will be ignored.'
        );
      }

      // Bug fix: send the CURRENT VIEW to the backend so exports match the
      // filtered + sorted rows the user is looking at — not the raw filter
      // params (which historically ignored search / brand / content type /
      // manager approval / date range / sort order). Pagination is
      // intentionally IGNORED so users get every row in scope, not just the
      // current page.
      const ids = sortedTasks.map(t => t.id).filter(Boolean);
      if (ids.length === 0) {
        toast.warning('No rows to export with the current filters.');
        return;
      }

      // Bug fix: task_ids used to be sent as a comma-separated URL query
      // param, which blew past Nginx's URI length limit (414 Request-URI
      // Too Large) once there were thousands of tasks. POST + JSON body
      // keeps the URL short regardless of how many rows are exported.
      const response = await axios.post(`${API}/tasks/export/${format}`, { task_ids: ids }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const fileExtension = format === 'excel' ? 'xlsx' : format;
      link.setAttribute('download', `tasks_export_${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      // Bug fix: the backend may export FEWER rows than the current view
      // if the caller's RBAC export scope is narrower than their view scope
      // (e.g. a non-manager's export is restricted to their own tasks).
      // Report the row count the server actually wrote, not what we asked
      // for, so the toast never claims a count that doesn't match the file.
      const exportedCount = parseInt(response.headers['x-export-row-count'], 10);
      if (!Number.isNaN(exportedCount) && exportedCount === 0) {
        toast.warning(`${format.toUpperCase()} exported 0 rows — none of the ${ids.length} visible task(s) are in your export permissions`, { duration: 8000 });
      } else if (!Number.isNaN(exportedCount) && exportedCount !== ids.length) {
        toast.success(`${format.toUpperCase()} exported — ${exportedCount} row(s) (of ${ids.length} visible, per your export permissions)`);
      } else {
        toast.success(`${format.toUpperCase()} exported successfully! (${Number.isNaN(exportedCount) ? ids.length : exportedCount} rows)`);
      }
    } catch (error) {
      // Bug fix: don't swallow the real backend error behind a generic
      // message — responseType:'blob' means axios puts the error body in
      // error.response.data as a Blob (even for JSON error responses), so
      // it has to be read back out as text before it's usable.
      let detail = error.message || 'Unknown error';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            detail = JSON.parse(text).detail || text;
          } catch {
            // Not JSON — likely a raw proxy/server HTML error page (e.g. Nginx
            // 414/413/502). Never surface raw HTML in the toast.
            const trimmed = (text || '').trim();
            if (trimmed.startsWith('<')) {
              const titleMatch = trimmed.match(/<title>(.*?)<\/title>/i);
              detail = titleMatch ? titleMatch[1] : `HTTP ${error.response?.status || 'error'}`;
            } else {
              detail = trimmed || detail;
            }
          }
        } else if (error.response?.data?.detail) {
          detail = error.response.data.detail;
        }
      } catch (parseErr) {
        console.error('Export error: failed to parse backend error response', parseErr);
      }
      console.error(`Export ${format.toUpperCase()} failed (HTTP ${error.response?.status}):`, detail, error);
      toast.error(`Failed to export ${format.toUpperCase()}: ${detail}`, { duration: 8000 });
    }
  };

  const exportCSV = () => {
    // Client-side fallback CSV — kept for legacy callers. Uses sortedTasks so
    // filters + sort are always respected.
    const headers = ['Task ID', 'Task', 'Status', 'Category', 'Output', 'Start Date'];
    const rows = sortedTasks.map(t => [
      t.task_id || '',
      t.task,
      t.your_status,
      t.category,
      t.output || '',
      t.start_date ? format(new Date(t.start_date), 'yyyy-MM-dd') : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${csvSafe(cell)}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('CSV exported!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-table-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Task Table</h1>
          <p className="page-subtitle">Manage all tasks with inline editing</p>
          {lastRefreshed && (
            <p
              className="text-xs text-slate-500 mt-1"
              title={lastRefreshed.toISOString()}
              data-testid="task-table-last-refreshed"
            >
              Last refreshed: {format(lastRefreshed, 'MMM d, yyyy • h:mm:ss a')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-busy={isRefreshing}
            data-testid="task-table-refresh-button"
          >
            <RefreshCw
              size={18}
              className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="export-dropdown-button"
                title={canExportAll
                  ? 'Export every task in the system. Filters and search are applied.'
                  : 'Your role can only export tasks assigned to you. Filters are applied within your own tasks.'}
              >
                <Download size={18} className="mr-2" />
                {canExportAll ? 'Export All Tasks' : 'Export My Tasks'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <div
                className="px-2 py-1.5 text-[11px] font-medium text-slate-500 border-b border-slate-100 mb-1"
                data-testid="export-scope-label"
              >
                {canExportAll
                  ? 'Scope: all users — filters applied'
                  : 'Scope: only your tasks — filters applied'}
              </div>
              <DropdownMenuItem onClick={() => handleServerExport('csv')} data-testid="export-as-csv">
                <FileSpreadsheet size={16} className="mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleServerExport('excel')} data-testid="export-as-excel">
                <FileSpreadsheet size={16} className="mr-2" />
                Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleServerExport('pdf')} data-testid="export-as-pdf">
                <FileText size={16} className="mr-2" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!isReadOnly && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-task-button" className="btn-cta">
                  <Plus size={18} className="mr-2" /> Add Task
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTask ? 'Edit Task' : 'Create New Task'}{' '}
                  {editingTask?.task_id && (
                    <span className="ml-1 text-sm font-medium text-slate-500">({editingTask.task_id})</span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="task-input">
                    Task Description <span className="text-red-500" aria-hidden="true">*</span>
                  </Label>
                  <Textarea
                    id="task-input"
                    value={formData.task}
                    onChange={(e) => {
                      setFormData({...formData, task: e.target.value});
                      if (formErrors.task) setFormErrors(({ task, ...rest }) => rest);
                    }}
                    rows={3}
                    data-testid="task-input"
                    aria-invalid={!!formErrors.task}
                    aria-describedby={formErrors.task ? 'task-input-error' : undefined}
                    className={formErrors.task ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {formErrors.task && (
                    <p id="task-input-error" data-testid="task-input-error" className="text-xs text-red-600 font-medium">
                      {formErrors.task}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Category <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => {
                        setFormData({...formData, category: v});
                        if (formErrors.category) setFormErrors(({ category, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="category-select"
                        aria-invalid={!!formErrors.category}
                        className={formErrors.category ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {TASK_CATEGORIES.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.category && (
                      <p data-testid="category-select-error" className="text-xs text-red-600 font-medium">
                        {formErrors.category}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Content Type</Label>
                    <Select value={formData.content_type || 'none'} onValueChange={(v) => setFormData({...formData, content_type: v === 'none' ? '' : v})}>
                      <SelectTrigger data-testid="content-type-select">
                        <SelectValue placeholder="Select Content Type (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Specified</SelectItem>
                        {CONTENT_TYPES.map(ct => (
                          <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Brand <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.brand || ''}
                      onValueChange={(v) => {
                        setFormData({...formData, brand: v});
                        if (formErrors.brand) setFormErrors(({ brand, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="brand-select"
                        aria-invalid={!!formErrors.brand}
                        className={formErrors.brand ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select Brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {(formData.brand && !BRANDS.includes(formData.brand)
                          ? [formData.brand, ...BRANDS]
                          : BRANDS
                        ).map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.brand && (
                      <p data-testid="brand-select-error" className="text-xs text-red-600 font-medium">
                        {formErrors.brand}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Status <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Select
                      value={formData.your_status}
                      onValueChange={(v) => {
                        setFormData({...formData, your_status: v});
                        if (formErrors.your_status) setFormErrors(({ your_status, ...rest }) => rest);
                      }}
                    >
                      <SelectTrigger
                        data-testid="status-select"
                        aria-invalid={!!formErrors.your_status}
                        className={formErrors.your_status ? 'border-red-500 focus:ring-red-500' : ''}
                      >
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {TASK_STATUSES.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.your_status && (
                      <p data-testid="status-select-error" className="text-xs text-red-600 font-medium">
                        {formErrors.your_status}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Assign To</Label>
                    <Select value={formData.task_done_by} onValueChange={(v) => setFormData({...formData, task_done_by: v})}>
                      <SelectTrigger data-testid="assign-user-select">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start-date-input">
                    Start Date <span className="text-red-500" aria-hidden="true">*</span>
                  </Label>
                  <DatePicker
                    value={formData.start_date || ''}
                    onChange={(val) => {
                      setFormData({...formData, start_date: val});
                      if (formErrors.start_date) setFormErrors(({ start_date, ...rest }) => rest);
                    }}
                    placeholder="Select start date"
                    testId="start-date-input"
                    className={formErrors.start_date ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {formErrors.start_date && (
                    <p id="start-date-input-error" data-testid="start-date-input-error" className="text-xs text-red-600 font-medium">
                      {formErrors.start_date}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Output</Label>
                  <Textarea
                    value={formData.output}
                    onChange={(e) => setFormData({...formData, output: e.target.value})}
                    rows={2}
                    data-testid="output-input"
                  />
                </div>
                
                {/* Multi-Link Input */}
                <div className="col-span-2">
                  <MultiLinkInput
                    links={formData.links}
                    onChange={(links) => setFormData({...formData, links})}
                    disabled={isReadOnly}
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.developer_dependency}
                    onChange={(e) => setFormData({...formData, developer_dependency: e.target.checked})}
                    data-testid="dev-dependency-checkbox"
                    className="w-4 h-4"
                  />
                  <Label>Developer Dependency</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.uiux_dependency}
                    onChange={(e) => setFormData({...formData, uiux_dependency: e.target.checked})}
                    data-testid="uiux-dependency-checkbox"
                    className="w-4 h-4"
                  />
                  <Label>UI/UX Dependency</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.manager_approval_required}
                    onChange={(e) => setFormData({...formData, manager_approval_required: e.target.checked})}
                    className="w-4 h-4"
                    data-testid="manager-approval-checkbox"
                  />
                  <Label>Manager Approval Required</Label>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-task-button">Save Task</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Filters</span>
          </div>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="clear-all-filters"
            >
              <X size={14} />
              Clear All Filters
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <Input
              placeholder="Search tasks or Task ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-tasks-input"
            />
          </div>
          <MultiSelectFilter
            label="Category"
            placeholder="All Categories"
            options={TASK_CATEGORIES}
            selectedValues={filterCategory}
            onChange={setFilterCategory}
            testId="filter-category"
          />
          <MultiSelectFilter
            label="Content Type"
            placeholder="All Content Types"
            options={CONTENT_TYPES}
            selectedValues={filterContentType}
            onChange={setFilterContentType}
            testId="filter-content-type"
          />
          <MultiSelectFilter
            label="Brand"
            placeholder="All Brands"
            options={BRANDS}
            selectedValues={filterBrand}
            onChange={setFilterBrand}
            testId="filter-brand"
          />
          <MultiSelectFilter
            label="Status"
            placeholder="All Statuses"
            options={TASK_STATUSES}
            selectedValues={filterStatus}
            onChange={setFilterStatus}
            testId="filter-status"
          />
          <MultiSelectFilter
            label="Assigned To"
            placeholder="All Users"
            options={[{ value: 'unassigned', label: 'Unassigned Tasks' }, ...users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))]}
            selectedValues={filterUser}
            onChange={setFilterUser}
            testId="filter-user"
          />
          <MultiSelectFilter
            label="Manager Approval"
            placeholder="All Approval Status"
            options={APPROVAL_STATUSES}
            selectedValues={filterManagerApproval}
            onChange={setFilterManagerApproval}
            testId="filter-manager-approval"
          />
        </div>
        
        {/* Second Row - Unified Dependency & Date Filters */}
        <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-slate-200">
          <MultiSelectFilter
            label="Dependency"
            placeholder="All Dependencies"
            selectAll={false}
            triggerClassName="w-[180px]"
            options={[
              { value: 'developer', label: `${depTypeLabel('developer', 'Developer')} Only` },
              { value: 'uiux', label: `${depTypeLabel('uiux', 'UI/UX')} Only` },
              { value: 'both', label: `${depTypeLabel('developer', 'Developer')} + ${depTypeLabel('uiux', 'UI/UX')}` },
              { value: 'none', label: 'No Dependency' },
            ]}
            selectedValues={filterDependency}
            onChange={setFilterDependency}
            testId="filter-dependency"
          />
          
          <div className="h-6 w-px bg-slate-300"></div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700">Date Range:</Label>
            <Select value={filterDateField} onValueChange={setFilterDateField}>
              <SelectTrigger className="w-[140px]" data-testid="filter-date-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="start_date">Start Date</SelectItem>
                <SelectItem value="created_at">Created Date</SelectItem>
                <SelectItem value="updated_at">Last Updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">From:</Label>
            <DatePicker
              value={filterFromDate}
              onChange={(val) => setFilterFromDate(val)}
              placeholder="From"
              testId="filter-from-date"
              className="w-40"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">To:</Label>
            <DatePicker
              value={filterToDate}
              onChange={(val) => setFilterToDate(val)}
              placeholder="To"
              testId="filter-to-date"
              className="w-40"
            />
          </div>
          
          {(filterFromDate || filterToDate) && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearDateFilters}
              className="flex items-center gap-1"
              data-testid="clear-date-filters"
            >
              <X size={14} />
              Clear Dates
            </Button>
          )}
          
          <div className="ml-auto text-sm font-medium text-slate-600">
            Showing <span className="text-cyan-600 font-bold">{sortedTasks.length}</span> of <span className="font-bold">{tasks.length}</span> tasks
          </div>
        </div>

        <ActiveFilterChips
          chips={[
            filterCategory.length > 0 && { key: 'category', label: `${filterCategory.length} Categor${filterCategory.length > 1 ? 'ies' : 'y'}`, onRemove: () => setFilterCategory([]) },
            filterContentType.length > 0 && { key: 'content-type', label: `${filterContentType.length} Content Type${filterContentType.length > 1 ? 's' : ''}`, onRemove: () => setFilterContentType([]) },
            filterBrand.length > 0 && { key: 'brand', label: `${filterBrand.length} Brand${filterBrand.length > 1 ? 's' : ''}`, onRemove: () => setFilterBrand([]) },
            filterStatus.length > 0 && { key: 'status', label: `${filterStatus.length} Status${filterStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterStatus([]) },
            filterUser.length > 0 && { key: 'user', label: `${filterUser.length} User${filterUser.length > 1 ? 's' : ''}`, onRemove: () => setFilterUser([]) },
            filterManagerApproval.length > 0 && { key: 'manager-approval', label: `${filterManagerApproval.length} Approval Status${filterManagerApproval.length > 1 ? 'es' : ''}`, onRemove: () => setFilterManagerApproval([]) },
            filterDependency.length > 0 && { key: 'dependency', label: `${filterDependency.length} Dependenc${filterDependency.length > 1 ? 'ies' : 'y'}`, onRemove: () => setFilterDependency([]) },
          ].filter(Boolean)}
          onClearAll={hasActiveFilters ? clearAllFilters : null}
        />
      </div>

      {/* Bulk Actions Bar */}
      {selectedTasks.length > 0 && isManager && !isReadOnly && (
        <div className="btn-cta rounded-xl shadow-lg p-4 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <span className="font-semibold text-lg">{selectedTasks.length} task(s) selected</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedTasks([])}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
              >
                <X size={14} className="mr-1" />
                Clear Selection
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={openBulkUpdateDialog}
                className="bg-white text-cyan-700 hover:bg-white/90 font-semibold"
                data-testid="bulk-update-button"
              >
                <Edit size={16} className="mr-2" />
                Bulk Update
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsReassignDialogOpen(true)}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                data-testid="bulk-reassign-button"
              >
                <UserCheck size={16} className="mr-2" />
                Reassign
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={requestBulkDelete}
                className="bg-red-500/90 text-white border-red-400 hover:bg-red-600"
                data-testid="bulk-delete-button"
              >
                <Trash size={16} className="mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* RBAC Feedback Banner */}
      {user && !can('tasks.approve') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="rbac-info-banner">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-sm text-blue-700">
            You are viewing tasks based on your role and assigned brands. Contact your manager for additional access.
          </p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full" style={{ tableLayout: 'auto' }}>
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {isManager && !isReadOnly && (
                  <th className="px-4 py-4 text-left w-12">
                    <Checkbox
                      checked={selectedTasks.length === paginatedTasks.length && paginatedTasks.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th
                  className="px-4 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 100, width: 100 }}
                  onClick={() => handleSort('task_id')}
                  data-testid="header-task-id"
                >
                  <div className="flex items-center gap-1">
                    Task ID
                    <SortIndicator field="task_id" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="relative text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors select-none whitespace-nowrap"
                  style={{ width: taskColumnWidth, minWidth: 200 }}
                >
                  <div 
                    className="px-6 py-4 flex items-center gap-1"
                    onClick={() => handleSort('task')}
                  >
                    Task
                    <SortIndicator field="task" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                  {/* Resize Handle */}
                  <div
                    className="absolute top-0 right-0 w-2 h-full cursor-col-resize bg-transparent hover:bg-cyan-400/50 transition-colors group"
                    onMouseDown={handleResizeStart}
                    onClick={(e) => e.stopPropagation()}
                    title="Drag to resize column"
                    data-testid="task-column-resize-handle"
                  >
                    <div className={`absolute top-1/2 -translate-y-1/2 right-0 w-1 h-10 rounded-full transition-all ${isResizing ? 'bg-cyan-500' : 'bg-slate-300 group-hover:bg-cyan-500'}`} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 160 }}
                  onClick={() => handleSort('task_done_by')}
                >
                  <div className="flex items-center gap-1">
                    User Name
                    <SortIndicator field="task_done_by" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 120 }}
                  onClick={() => handleSort('start_date')}
                >
                  <div className="flex items-center gap-1">
                    Start Date
                    <SortIndicator field="start_date" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 110 }}
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center gap-1">
                    Category
                    <SortIndicator field="category" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 110 }}
                  onClick={() => handleSort('brand')}
                >
                  <div className="flex items-center gap-1">
                    Brand
                    <SortIndicator field="brand" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 100 }}
                  onClick={() => handleSort('your_status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIndicator field="your_status" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 140 }}
                  onClick={() => handleSort('manager_approval_status')}
                >
                  <div className="flex items-center gap-1">
                    Manager Approval
                    <SortIndicator field="manager_approval_status" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 100 }}
                  onClick={() => handleSort('output')}
                >
                  <div className="flex items-center gap-1">
                    Output
                    <SortIndicator field="output" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase whitespace-nowrap" style={{ minWidth: 80 }}>Notes</th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
                  style={{ minWidth: 170 }}
                  onClick={() => handleSort('dependency')}
                  data-testid="header-dependency"
                >
                  <div className="flex items-center gap-1">
                    Dependency
                    <SortIndicator field="dependency" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                {!isReadOnly && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase whitespace-nowrap" style={{ minWidth: 110 }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedTasks.map((task) => {
                const assignedUser = users.find(u => u.id === task.task_done_by);
                return (
                  <tr 
                    key={task.id} 
                    className="hover:bg-slate-50 cursor-pointer" 
                    data-testid={`task-row-${task.id}`}
                    onClick={(e) => {
                      // Don't open modal if clicking on action buttons, checkboxes, or select dropdowns
                      if (
                        e.target.closest('button') || 
                        e.target.closest('[role="checkbox"]') ||
                        e.target.closest('[role="combobox"]')
                      ) {
                        return;
                      }
                      setSelectedTask(task);
                      setIsDetailOpen(true);
                    }}
                  >
                    {isManager && !isReadOnly && (
                      <td className="px-4 py-4 w-12">
                        <Checkbox
                          checked={selectedTasks.includes(task.id)}
                          onCheckedChange={() => toggleTaskSelection(task.id)}
                          data-testid={`task-checkbox-${task.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap" style={{ minWidth: 100, width: 100 }} data-testid={`task-id-cell-${task.id}`}>
                      <TaskIdBadge taskId={task.task_id} />
                    </td>
                    <td 
                      className="px-6 py-4"
                      style={{ width: taskColumnWidth, minWidth: 200 }}
                    >
                      <div className="max-w-md">
                        <p className="font-medium text-slate-900 whitespace-pre-wrap break-words">{task.task}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 160 }}>
                      {isManager ? (
                        <Select
                          value={task.task_done_by || ''}
                          onValueChange={(value) => handleSingleReassign(task.id, value)}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Assign user">
                              {assignedUser ? (
                                <UserAvatar user={assignedUser} size="sm" showName />
                              ) : (
                                <span className="text-sm text-slate-400">Unassigned</span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                <UserAvatar user={u} size="sm" showName />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          {assignedUser ? (
                            <UserAvatar user={assignedUser} size="md" showName />
                          ) : (
                            <span className="text-sm text-slate-400 italic">Unassigned</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 120 }}>
                      {task.start_date ? (
                        <span className="text-sm text-slate-700 whitespace-nowrap">
                          {format(new Date(task.start_date), 'MMM dd, yyyy')}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 110 }}>
                      <div className="flex flex-wrap gap-1">
                        <span className={`inline-block px-3 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${
                          task.category === 'Technical SEO' ? 'bg-purple-100 text-purple-700' :
                          task.category === 'On-Page' ? 'bg-blue-100 text-blue-700' :
                          task.category === 'Off-Page' ? 'bg-pink-100 text-pink-700' :
                          task.category === 'UI/UX Design' ? 'bg-indigo-100 text-indigo-700' :
                          task.category === 'SEO' ? 'bg-green-100 text-green-700' :
                          task.category === 'QA/Technical' ? 'bg-orange-100 text-orange-700' :
                          task.category === 'Google Ads' ? 'bg-red-100 text-red-700' :
                          task.category === 'Meta Ads' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {task.category}
                        </span>
                        {task.content_type && (
                          <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getContentTypeBadgeClasses(task.content_type)}`}>
                            {task.content_type}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 110 }}>
                      {task.brand ? (
                        <span className={`inline-block px-3 py-1 rounded-md text-xs font-semibold border whitespace-nowrap ${getBrandBadgeClasses(task.brand)}`}>
                          {task.brand}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 100 }}>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        task.your_status === 'Completed' ? 'bg-green-100 text-green-700' :
                        task.your_status === 'In Process' ? 'bg-blue-100 text-blue-700' :
                        task.your_status === 'On Hold' ? 'bg-red-100 text-red-700' :
                        task.your_status === 'Backlog' ? 'bg-slate-100 text-slate-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {task.your_status}
                      </span>
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 140 }}>
                      {task.manager_approval_required ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          task.manager_approval_status === 'Approved' ? 'bg-green-100 text-green-700' :
                          task.manager_approval_status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          task.manager_approval_status === 'Standby' ? 'bg-amber-100 text-amber-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {task.manager_approval_status === 'Approved' ? '✅ Approved' :
                           task.manager_approval_status === 'Rejected' ? '❌ Rejected' :
                           task.manager_approval_status === 'Standby' ? '⏸️ Standby' :
                           '⏳ Pending'}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 100 }}>
                      <div className="max-w-[120px]">
                        <p className="text-sm text-slate-600 truncate">{task.output || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 80 }}>
                      <div className="max-w-[100px]">
                        <p className="text-sm text-slate-600 truncate">{task.notes || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4" style={{ minWidth: 170 }} data-testid={`dependency-cell-${task.id}`}>
                      {(task.developer_dependency || task.uiux_dependency) ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {task.developer_dependency && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap"
                              data-testid={`dependency-badge-dev-${task.id}`}
                            >
                              <Code size={12} />
                              Developer
                            </span>
                          )}
                          {task.uiux_dependency && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 whitespace-nowrap"
                              data-testid={`dependency-badge-uiux-${task.id}`}
                            >
                              <Palette size={12} />
                              UI/UX
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                    {!isReadOnly && (
                      <td className="px-6 py-4" style={{ minWidth: 110 }}>
                        <div className="flex items-center gap-1">
                          {(() => {
                            const primaryUrl = task.links?.[0]?.url || task.link;
                            if (!primaryUrl) return null;
                            return (
                              <a
                                href={primaryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open Link"
                                aria-label="Open link"
                                data-testid={`open-link-${task.id}`}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5"
                              >
                                <ExternalLink size={16} />
                              </a>
                            );
                          })()}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(task)}
                            data-testid={`edit-task-${task.id}`}
                            title="Edit"
                            className="h-8 w-8 p-0"
                          >
                            <Edit size={16} />
                          </Button>
                          {isManager && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestDelete(task.id)}
                              data-testid={`delete-task-${task.id}`}
                              title="Delete"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                </tr>
                );
              })}
            </tbody>
          </table>
          {paginatedTasks.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">No tasks found</p>
            </div>
          )}
        </div>
        
        {/* Pagination Controls */}
        {sortedTasks.length > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between">
              {/* Rows per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(Number(v))}>
                  <SelectTrigger className="w-[70px] h-8" data-testid="rows-per-page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">rows</span>
              </div>

              {/* Showing X to Y of Z entries */}
              <div className="text-sm text-slate-600">
                Showing <span className="font-semibold text-slate-900">{startIndex + 1}</span> to{' '}
                <span className="font-semibold text-slate-900">{Math.min(endIndex, sortedTasks.length)}</span> of{' '}
                <span className="font-semibold text-slate-900">{sortedTasks.length}</span> entries
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8"
                  data-testid="prev-page"
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm text-slate-600">Page</span>
                  <span className="text-sm font-semibold text-slate-900">{currentPage}</span>
                  <span className="text-sm text-slate-600">of</span>
                  <span className="text-sm font-semibold text-slate-900">{totalPages || 1}</span>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="h-8"
                  data-testid="next-page"
                >
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Reassign Dialog */}
      <Dialog open={isReassignDialogOpen} onOpenChange={setIsReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign {selectedTasks.length} Task(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <p className="text-sm text-cyan-800">
                You are about to reassign <strong>{selectedTasks.length}</strong> task(s) to a new user. This action will update the assignment for all selected tasks.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Select New Assignee</Label>
              <Select value={reassignUserId} onValueChange={setReassignUserId}>
                <SelectTrigger data-testid="bulk-reassign-select">
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <UserAvatar user={u} size="sm" showName />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsReassignDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkReassign}
                className="btn-cta"
                data-testid="confirm-reassign-button"
              >
                <UserCheck size={16} className="mr-2" />
                Confirm Reassignment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedTask(null);
        }}
        users={users}
        onTaskUpdated={loadData}
        onEdit={(task) => {
          setIsDetailOpen(false);
          setSelectedTask(null);
          openEditDialog(task);
        }}
      />

      {/* Bulk Update Dialog */}
      <Dialog open={isBulkUpdateDialogOpen} onOpenChange={setIsBulkUpdateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              Bulk Update Tasks
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <p className="text-sm text-cyan-800">
                <strong>{selectedTasks.length}</strong> task(s) selected. Check the fields you want to update. Unchecked fields will remain unchanged.
              </p>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-status"
                  checked={bulkUpdateFields.your_status.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    your_status: { ...bulkUpdateFields.your_status, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-status" className="font-semibold">Status</Label>
              </div>
              {bulkUpdateFields.your_status.enabled && (
                <Select 
                  value={bulkUpdateFields.your_status.value} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    your_status: { ...bulkUpdateFields.your_status, value: v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Assigned User */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-user"
                  checked={bulkUpdateFields.task_done_by.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    task_done_by: { ...bulkUpdateFields.task_done_by, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-user" className="font-semibold">Assigned User</Label>
              </div>
              {bulkUpdateFields.task_done_by.enabled && (
                <Select 
                  value={bulkUpdateFields.task_done_by.value} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    task_done_by: { ...bulkUpdateFields.task_done_by, value: v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-user-select">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-category"
                  checked={bulkUpdateFields.category.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    category: { ...bulkUpdateFields.category, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-category" className="font-semibold">Category</Label>
              </div>
              {bulkUpdateFields.category.enabled && (
                <Select 
                  value={bulkUpdateFields.category.value} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    category: { ...bulkUpdateFields.category, value: v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Content Type */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-content-type"
                  checked={bulkUpdateFields.content_type.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    content_type: { ...bulkUpdateFields.content_type, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-content-type" className="font-semibold">Content Type</Label>
              </div>
              {bulkUpdateFields.content_type.enabled && (
                <Select 
                  value={bulkUpdateFields.content_type.value || 'none'} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    content_type: { ...bulkUpdateFields.content_type, value: v === 'none' ? '' : v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-content-type-select">
                    <SelectValue placeholder="Select content type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Specified</SelectItem>
                    {CONTENT_TYPES.map(ct => (
                      <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Brand */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-brand"
                  checked={bulkUpdateFields.brand.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    brand: { ...bulkUpdateFields.brand, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-brand" className="font-semibold">Brand</Label>
              </div>
              {bulkUpdateFields.brand.enabled && (
                <Select 
                  value={bulkUpdateFields.brand.value || 'none'} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    brand: { ...bulkUpdateFields.brand, value: v === 'none' ? '' : v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-brand-select">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Brand</SelectItem>
                    {BRANDS.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Manager Approval Status */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-approval-status"
                  checked={bulkUpdateFields.manager_approval_status.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    manager_approval_status: { ...bulkUpdateFields.manager_approval_status, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-approval-status" className="font-semibold">Manager Approval Status</Label>
              </div>
              {bulkUpdateFields.manager_approval_status.enabled && (
                <Select 
                  value={bulkUpdateFields.manager_approval_status.value} 
                  onValueChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    manager_approval_status: { ...bulkUpdateFields.manager_approval_status, value: v }
                  })}
                >
                  <SelectTrigger data-testid="bulk-approval-select">
                    <SelectValue placeholder="Select approval status" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVAL_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-start-date"
                  checked={bulkUpdateFields.start_date.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    start_date: { ...bulkUpdateFields.start_date, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-start-date" className="font-semibold">Start Date</Label>
              </div>
              {bulkUpdateFields.start_date.enabled && (
                <DatePicker
                  value={bulkUpdateFields.start_date.value}
                  onChange={(v) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    start_date: { ...bulkUpdateFields.start_date, value: v }
                  })}
                  testId="bulk-start-date"
                />
              )}
            </div>

            {/* Dependency Flags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="bulk-dev-dep"
                    checked={bulkUpdateFields.developer_dependency.enabled}
                    onCheckedChange={(checked) => setBulkUpdateFields({
                      ...bulkUpdateFields,
                      developer_dependency: { ...bulkUpdateFields.developer_dependency, enabled: checked }
                    })}
                  />
                  <Label htmlFor="bulk-dev-dep" className="font-semibold">Developer Dependency</Label>
                </div>
                {bulkUpdateFields.developer_dependency.enabled && (
                  <div className="flex items-center gap-2 ml-6">
                    <Checkbox
                      id="bulk-dev-dep-value"
                      checked={bulkUpdateFields.developer_dependency.value}
                      onCheckedChange={(checked) => setBulkUpdateFields({
                        ...bulkUpdateFields,
                        developer_dependency: { ...bulkUpdateFields.developer_dependency, value: checked }
                      })}
                    />
                    <Label htmlFor="bulk-dev-dep-value" className="text-sm">Enable dependency</Label>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="bulk-uiux-dep"
                    checked={bulkUpdateFields.uiux_dependency.enabled}
                    onCheckedChange={(checked) => setBulkUpdateFields({
                      ...bulkUpdateFields,
                      uiux_dependency: { ...bulkUpdateFields.uiux_dependency, enabled: checked }
                    })}
                  />
                  <Label htmlFor="bulk-uiux-dep" className="font-semibold">UI/UX Dependency</Label>
                </div>
                {bulkUpdateFields.uiux_dependency.enabled && (
                  <div className="flex items-center gap-2 ml-6">
                    <Checkbox
                      id="bulk-uiux-dep-value"
                      checked={bulkUpdateFields.uiux_dependency.value}
                      onCheckedChange={(checked) => setBulkUpdateFields({
                        ...bulkUpdateFields,
                        uiux_dependency: { ...bulkUpdateFields.uiux_dependency, value: checked }
                      })}
                    />
                    <Label htmlFor="bulk-uiux-dep-value" className="text-sm">Enable dependency</Label>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-notes"
                  checked={bulkUpdateFields.notes.enabled}
                  onCheckedChange={(checked) => setBulkUpdateFields({
                    ...bulkUpdateFields,
                    notes: { ...bulkUpdateFields.notes, enabled: checked }
                  })}
                />
                <Label htmlFor="bulk-notes" className="font-semibold">Notes</Label>
              </div>
              {bulkUpdateFields.notes.enabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="notes-mode"
                        checked={bulkUpdateFields.notes.mode === 'append'}
                        onChange={() => setBulkUpdateFields({
                          ...bulkUpdateFields,
                          notes: { ...bulkUpdateFields.notes, mode: 'append' }
                        })}
                        className="text-cyan-600"
                      />
                      <span className="text-sm">Append to existing notes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="notes-mode"
                        checked={bulkUpdateFields.notes.mode === 'overwrite'}
                        onChange={() => setBulkUpdateFields({
                          ...bulkUpdateFields,
                          notes: { ...bulkUpdateFields.notes, mode: 'overwrite' }
                        })}
                        className="text-cyan-600"
                      />
                      <span className="text-sm text-orange-600 font-medium">Overwrite existing notes</span>
                    </label>
                  </div>
                  <Textarea
                    value={bulkUpdateFields.notes.value}
                    onChange={(e) => setBulkUpdateFields({
                      ...bulkUpdateFields,
                      notes: { ...bulkUpdateFields.notes, value: e.target.value }
                    })}
                    placeholder="Enter notes to add..."
                    rows={3}
                    data-testid="bulk-notes-textarea"
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={closeBulkUpdateDialog}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkUpdateConfirm}
                className="btn-cta"
                disabled={!hasEnabledBulkFields()}
                data-testid="bulk-update-preview-button"
              >
                Preview Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Confirmation Dialog */}
      <Dialog open={isBulkConfirmDialogOpen} onOpenChange={setIsBulkConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              Confirm Bulk Update
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-semibold mb-2">
                ⚠️ This action cannot be undone
              </p>
              <p className="text-sm text-yellow-700">
                You are about to update <strong>{selectedTasks.length}</strong> task(s).
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">Fields to be updated:</p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                {Object.entries(getEnabledBulkFields()).map(([field, value]) => (
                  <div key={field} className="flex justify-between text-sm">
                    <span className="text-slate-600">{getBulkFieldLabel(field)}:</span>
                    <span className="font-medium text-slate-900">
                      {getBulkFieldValue(field, value) || '(empty)'}
                    </span>
                  </div>
                ))}
                {bulkUpdateFields.notes.enabled && (
                  <div className="text-xs text-slate-500 pt-1 border-t border-slate-200">
                    Notes mode: {bulkUpdateFields.notes.mode === 'append' ? 'Append' : 'Overwrite'}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsBulkConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkUpdateSubmit}
                className="btn-cta"
                disabled={bulkUpdateLoading}
                data-testid="bulk-update-confirm-button"
              >
                {bulkUpdateLoading ? 'Updating...' : `Update ${selectedTasks.length} Tasks`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog — replaces window.confirm for single + bulk delete */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, mode: null, taskId: null })}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConfirm.mode === 'bulk' ? `Delete ${selectedTasks.length} task(s)?` : 'Delete this task?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm.mode === 'bulk'
                ? `This will permanently delete ${selectedTasks.length} selected task(s). This action cannot be undone.`
                : 'This will permanently delete the task. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-submit"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={confirmDeleteDialog}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaskTable;